import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";

const TOOLS = [
  { cmd: "bold", label: "B", title: "Bold", className: "font-extrabold" },
  { cmd: "italic", label: "I", title: "Italic", className: "italic font-serif text-lg" },
  { cmd: "formatBlock", value: "h2", label: "H2", title: "Heading 2" },
  { cmd: "formatBlock", value: "h3", label: "H3", title: "Heading 3" },
  { cmd: "insertUnorderedList", label: "• List", title: "Bulleted list" },
  { cmd: "insertOrderedList", label: "1. List", title: "Numbered list" },
  { cmd: "formatBlock", value: "blockquote", label: "Quote", title: "Blockquote" },
  { cmd: "removeFormat", label: "Clear", title: "Clear formatting" },
];

export default function RichTextEditor({ value, onChange, placeholder = "Write here…" }) {
  const ref = useRef(null);
  const lastEmitted = useRef(value);
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (ref.current && !ref.current.innerHTML && value !== lastEmitted.current) {
      ref.current.innerHTML = value || "";
      lastEmitted.current = value;
    }
  }, [value]);

  const exec = (e, tool) => {
    e.preventDefault();
    ref.current?.focus();
    document.execCommand(tool.cmd, false, tool.value || null);
    handleInput();
  };

  const handleInput = () => {
    lastEmitted.current = ref.current?.innerHTML || "";
    onChange(lastEmitted.current);
  };

  const insertLink = (e) => {
    e.preventDefault();
    const url = window.prompt("Link URL (https://…)");
    if (url) {
      ref.current?.focus();
      document.execCommand("createLink", false, url);
      handleInput();
    }
  };

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/upload", fd);
      ref.current?.focus();
      document.execCommand("insertHTML", false, `<img src="${data.url}" alt="" />`);
      handleInput();
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  return (
    <div className="border border-obsidian/20 bg-white" data-testid="rich-text-editor">
      <div className="flex flex-wrap items-center gap-1 border-b border-obsidian/10 bg-ash/50 px-2 py-1.5">
        {TOOLS.map((t) => (
          <button
            key={t.label}
            type="button"
            title={t.title}
            onMouseDown={(e) => exec(e, t)}
            className={`px-2.5 py-1 text-[12px] font-semibold hover:bg-paper ${t.className || ""}`}
          >
            {t.label}
          </button>
        ))}
        <button type="button" title="Insert link" onMouseDown={insertLink} className="px-2.5 py-1 text-[12px] font-semibold text-progress hover:bg-paper">
          Link
        </button>
        <button
          type="button"
          title="Upload image"
          disabled={busy}
          onClick={() => fileRef.current?.click()}
          className="px-2.5 py-1 text-[12px] font-semibold text-progress hover:bg-paper disabled:opacity-50"
        >
          {busy ? "Uploading…" : "Image"}
        </button>
        <input ref={fileRef} type="file" accept="image/*" hidden onChange={uploadImage} />
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={handleInput}
        onBlur={handleInput}
        data-placeholder={placeholder}
        className="rte rte-editor p-5"
      />
    </div>
  );
}
